import React from 'react';
import loadable from '@loadable/component';

import getPageDataLoadingAPI from '../containers/pageDataLoadingAPI';
import NotFoundPage from '../containers/NotFoundPage/NotFoundPage';
import PreviewResolverPage from '../containers/PreviewResolverPage/PreviewResolverPage';

// routeConfiguration needs to initialize containers first
// Otherwise, components will import form container eventually and
// at that point css bundling / imports will happen in wrong order.
import { NamedRedirect } from '../components';
import CoffeeToolsPage from '../containers/CoffeeToolsPage/CoffeeToolsPage';

const LikedPage = loadable(() =>
  import(/* webpackChunkName: "LikedPage" */ '../containers/LikedPage/LikedPage')
);
const BuyerAccountPage = loadable(() =>
  import(
    /* webpackChunkName: "BuyerAccountPage" */ '../containers/BuyerAccountPage/BuyerAccountPage'
  )
);
const ResetPasswordPage = loadable(() => import(/* webpackChunkName: "ResetPasswordPage" */ '../containers/ResetPasswordPage/ResetPasswordPage'));
const BrewLabPage = loadable(() => import(/* webpackChunkName: "BrewLabPage" */ '../containers/BrewLabPage/BrewLabPage'));
const BuyerPreferencesPage = loadable(() =>
  import(
    /* webpackChunkName: "BuyerPreferencesPage" */ '../containers/BuyerPreferencesPage/BuyerPreferencesPage'
  )
);

const ProductsPage = loadable(() =>
  import(
    /* webpackChunkName: "ProductsPage" */ '../containers/ProductsPage/ProductsPage'
  )
);

const RoasterIntelligenceAccountPage = loadable(() =>
  import(/* webpackChunkName: "RoasterIntelligenceAccountPage" */ '../containers/RoasterIntelligenceAccountPage/RoasterIntelligenceAccountPage')
);

const PrivateLabelPage = loadable(() => import(/* webpackChunkName: "PrivateLabelPage" */ '../containers/PrivateLabelPage/PrivateLabelPage'));

const RoasterIntelligencePage = loadable(() =>
  import(/* webpackChunkName: "RoasterIntelligencePage" */ '../containers/RoasterIntelligencePage/RoasterIntelligencePage')
);

const CoffeeDataPage = loadable(() =>
  import(
    /* webpackChunkName: "CoffeeDataPage" */ '../containers/CoffeeDataPage/CoffeeDataPage'
  )
);

const SeoCityPage = loadable(() =>
  import(
    /* webpackChunkName: "SeoCityPage" */ '../containers/SeoCityPage/SeoCityPage'
  )
);

const CoffeeGuidesHubPage = loadable(() =>
  import(/* webpackChunkName: "CoffeeGuidesHubPage" */ '../containers/CoffeeGuidesHubPage/CoffeeGuidesHubPage')
);

const CoffeeGuidePage = loadable(() =>
  import(
    /* webpackChunkName: "CoffeeGuidePage" */ '../containers/CoffeeGuidePage/CoffeeGuidePage'
  )
);

const SeoCollectionPage = loadable(() =>
  import(
    /* webpackChunkName: "SeoCollectionPage" */ '../containers/SeoCollectionPage/SeoCollectionPage'
  )
);

const TrafficDashboardPage = loadable(() =>
  import(
    /* webpackChunkName: "TrafficDashboardPage" */ '../containers/TrafficDashboardPage/TrafficDashboardPage'
  )
);

const pageDataLoadingAPI = getPageDataLoadingAPI();

const AuthenticationPage = loadable(() =>
  import(
    /* webpackChunkName: "AuthenticationPage" */ '../containers/AuthenticationPage/AuthenticationPage'
  )
);
const CMSPage = loadable(() =>
  import(/* webpackChunkName: "CMSPage" */ '../containers/CMSPage/CMSPage')
);
const ContactDetailsPage = loadable(() =>
  import(
    /* webpackChunkName: "ContactDetailsPage" */ '../containers/ContactDetailsPage/ContactDetailsPage'
  )
);
const EmailVerificationPage = loadable(() =>
  import(
    /* webpackChunkName: "EmailVerificationPage" */ '../containers/EmailVerificationPage/EmailVerificationPage'
  )
);
const LandingPage = loadable(() =>
  import(/* webpackChunkName: "LandingPage" */ '../containers/LandingPage/LandingPage')
);
const ListingPageCoverPhoto = loadable(() =>
  import(
    /* webpackChunkName: "ListingPageCoverPhoto" */ /* webpackPrefetch: true */ '../containers/ListingPage/ListingPageCoverPhoto'
  )
);
const ListingPageCarousel = loadable(() =>
  import(
    /* webpackChunkName: "ListingPageCarousel" */ /* webpackPrefetch: true */ '../containers/ListingPage/ListingPageCarousel'
  )
);
const ManageAccountPage = loadable(() =>
  import(
    /* webpackChunkName: "ManageAccountPage" */ '../containers/ManageAccountPage/ManageAccountPage'
  )
);
const PasswordChangePage = loadable(() =>
  import(
    /* webpackChunkName: "PasswordChangePage" */ '../containers/PasswordChangePage/PasswordChangePage'
  )
);
const PasswordRecoveryPage = loadable(() =>
  import(
    /* webpackChunkName: "PasswordRecoveryPage" */ '../containers/PasswordRecoveryPage/PasswordRecoveryPage'
  )
);
const PasswordResetPage = loadable(() =>
  import(
    /* webpackChunkName: "PasswordResetPage" */ '../containers/PasswordResetPage/PasswordResetPage'
  )
);
const PaymentMethodsPage = loadable(() =>
  import(
    /* webpackChunkName: "PaymentMethodsPage" */ '../containers/PaymentMethodsPage/PaymentMethodsPage'
  )
);
const PrivacyPolicyPage = loadable(() =>
  import(
    /* webpackChunkName: "PrivacyPolicyPage" */ '../containers/PrivacyPolicyPage/PrivacyPolicyPage'
  )
);
const ProfilePage = loadable(() =>
  import(/* webpackChunkName: "ProfilePage" */ '../containers/ProfilePage/ProfilePage')
);
const ProfileSettingsPage = loadable(() =>
  import(
    /* webpackChunkName: "ProfileSettingsPage" */ '../containers/ProfileSettingsPage/ProfileSettingsPage'
  )
);
const SearchPageWithMap = loadable(() =>
  import(
    /* webpackChunkName: "SearchPageWithMap" */ /* webpackPrefetch: true */ '../containers/SearchPage/SearchPageWithMap'
  )
);
const SearchPageWithGrid = loadable(() =>
  import(
    /* webpackChunkName: "SearchPageWithGrid" */ /* webpackPrefetch: true */ '../containers/SearchPage/SearchPageWithGrid'
  )
);
const TermsOfServicePage = loadable(() =>
  import(
    /* webpackChunkName: "TermsOfServicePage" */ '../containers/TermsOfServicePage/TermsOfServicePage'
  )
);
const NoAccessPage = loadable(() =>
  import(/* webpackChunkName: "NoAccessPage" */ '../containers/NoAccessPage/NoAccessPage')
);

// Styleguide helps you to review current components and develop new ones
const StyleguidePage = loadable(() =>
  import(/* webpackChunkName: "StyleguidePage" */ '../containers/StyleguidePage/StyleguidePage')
);

// Buyer-only account settings pages
export const ACCOUNT_SETTINGS_PAGES = [
  'ContactDetailsPage',
  'PasswordChangePage',
  'PaymentMethodsPage',
  'BuyerAccountPage',
  'BuyerPreferencesPage',
  'LikedPage',
];

const RedirectToLandingPage = () => <NamedRedirect name="LandingPage" />;
const RedirectToSearchPage = () => <NamedRedirect name="SearchPage" />;

// Our routes are exact by default.
// See behaviour from Routes.js where Route is created.
const routeConfiguration = (layoutConfig = {}, accessControlConfig = {}) => {
  const searchVariant = layoutConfig?.searchPage?.variantType || 'map';
  const listingVariant = layoutConfig?.listingPage?.variantType;

  const SearchPage = searchVariant === 'map' ? SearchPageWithMap : SearchPageWithGrid;
  const ListingPage = listingVariant === 'carousel' ? ListingPageCarousel : ListingPageCoverPhoto;

  const isPrivateMarketplace =
    accessControlConfig?.marketplace?.private === true ||
    accessControlConfig?.marketplace?.isPrivate === true;

  const authForPrivateMarketplace = isPrivateMarketplace ? { auth: true } : {};

  return [
    // Home
    {
      path: '/',
      name: 'LandingPage',
      component: RedirectToSearchPage,
    },

    // Guides hub precedes the generic city slug route.
    {
      path: '/coffee/guides',
      exact: true,
      name: 'CoffeeGuidesHubPage',
      component: CoffeeGuidesHubPage,
    },
    // SEO collection pages
    {
      path: '/coffee/guides/:slug',
      name: 'CoffeeGuidePage',
      component: CoffeeGuidePage,
    },
    {
      path: '/coffee/collections/:slug',
      name: 'SeoCollectionPage',
      component: SeoCollectionPage,
    },

    // Products demand test
    {
      path: '/products',
      name: 'ProductsPage',
      component: ProductsPage,
    },

    // Brew Intelligence — private account-backed prototype
    {
      path: '/brew',
      name: 'BrewLabPage',
      component: BrewLabPage,
    },

    {
      path: '/for-roasters/',
      name: 'RoasterIntelligencePage',
      component: RoasterIntelligencePage,
    },

    {
      path: '/roasters/intelligence/account',
      name: 'RoasterIntelligenceAccountPage',
      component: RoasterIntelligenceAccountPage,
    },

    {
      path: '/private-label/',
      name: 'PrivateLabelPage',
      component: PrivateLabelPage,
    },

    // Coffee data
    {
      path: '/coffee/data/independent-coffee-price-index',
      name: 'CoffeePriceIndexPage',
      component: CoffeeDataPage,
    },

    // Coffee tools
    {
      path: '/coffee/tools',
      name: 'CoffeeToolsIndexPage',
      component: CoffeeToolsPage,
      exact: true,
    },
    {
      path: '/coffee/tools/:slug',
      name: 'CoffeeToolPage',
      component: CoffeeToolsPage,
    },

    // City SEO pages
    {
      path: '/coffee/:slug',
      name: 'SeoCityPage',
      component: SeoCityPage,
    },
    
    // CMS
    {
      path: '/p/:pageId',
      name: 'CMSPage',
      component: CMSPage,
      loadData: pageDataLoadingAPI.CMSPage?.loadData,
    },

    // Search
    {
      path: '/s',
      name: 'SearchPage',
      ...authForPrivateMarketplace,
      component: SearchPage,
      loadData: pageDataLoadingAPI.SearchPage?.loadData,
    },
    {
      path: '/s/:listingType',
      name: 'SearchPageWithListingType',
      ...authForPrivateMarketplace,
      component: SearchPage,
      loadData: pageDataLoadingAPI.SearchPage?.loadData,
    },

    // Listing (discovery only)
    {
      path: '/l',
      name: 'ListingBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/l/:slug/:id',
      name: 'ListingPage',
      ...authForPrivateMarketplace,
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage?.loadData,
    },
    {
      path: '/l/:slug/:id/:variant',
      name: 'ListingPageVariant',
      ...authForPrivateMarketplace,
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage?.loadData,
    },

    // Discovery-only MVP: kill transactiony routes
    {
      path: '/l/:slug/:id/make-offer',
      name: 'MakeOfferPage',
      component: RedirectToLandingPage,
    },
    {
      path: '/l/:slug/:id/request-quote',
      name: 'RequestQuotePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/l/:slug/:id/checkout',
      name: 'CheckoutPage',
      component: RedirectToLandingPage,
    },

    // Buyer-only MVP: block seller listing creation/edit flows
    {
      path: '/l/new',
      name: 'NewListingPage',
      component: RedirectToLandingPage,
    },
    {
      path: '/l/:slug/:id/:type/:tab',
      name: 'EditListingPage',
      component: RedirectToLandingPage,
    },
    {
      path: '/l/:slug/:id/:type/:tab/:returnURLType',
      name: 'EditListingStripeOnboardingPage',
      component: RedirectToLandingPage,
    },

    // Canonical path should be after /l/new since they conflict
    {
      path: '/l/:id',
      name: 'ListingPageCanonical',
      ...authForPrivateMarketplace,
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage?.loadData,
    },

    // Profiles
    {
      path: '/u',
      name: 'ProfileBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/u/:id',
      name: 'ProfilePage',
      ...authForPrivateMarketplace,
      component: ProfilePage,
      loadData: pageDataLoadingAPI.ProfilePage?.loadData,
    },
    {
      path: '/u/:id/:variant',
      name: 'ProfilePageVariant',
      auth: true,
      component: ProfilePage,
      loadData: pageDataLoadingAPI.ProfilePage?.loadData,
    },

    // Buyer profile settings
    {
      path: '/profile-settings',
      name: 'ProfileSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="BuyerAccountPage" />,
    },
    // Auth
    {
      path: '/login',
      name: 'LoginPage',
      component: AuthenticationPage,
      extraProps: { tab: 'login' },
    },
    {
      path: '/signup',
      name: 'SignupPage',
      component: AuthenticationPage,
      extraProps: { tab: 'signup' },
      loadData: pageDataLoadingAPI.AuthenticationPage?.loadData,
    },
    {
      path: '/signup/:userType',
      name: 'SignupForUserTypePage',
      component: AuthenticationPage,
      extraProps: { tab: 'signup' },
      loadData: pageDataLoadingAPI.AuthenticationPage?.loadData,
    },
    {
      path: '/confirm',
      name: 'ConfirmPage',
      component: AuthenticationPage,
      extraProps: { tab: 'confirm' },
      loadData: pageDataLoadingAPI.AuthenticationPage?.loadData,
    },
    {
      path: '/recover-password',
      name: 'PasswordRecoveryPage',
      component: PasswordRecoveryPage,
    },

    // Buyer-only MVP: remove inbox entirely
    {
      path: '/inbox',
      name: 'InboxBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/inbox/:tab',
      name: 'InboxPage',
      component: RedirectToLandingPage,
    },

    // Buyer-only MVP: remove order/sale routes entirely
    {
      path: '/order/:id',
      name: 'OrderDetailsPage',
      component: RedirectToLandingPage,
    },
    {
      path: '/order/:id/details',
      name: 'OrderDetailsPageRedirect',
      component: RedirectToLandingPage,
    },
    {
      path: '/sale/:id',
      name: 'SaleDetailsPage',
      component: RedirectToLandingPage,
    },
    {
      path: '/sale/:id/details',
      name: 'SaleDetailsPageRedirect',
      component: RedirectToLandingPage,
    },

    // Buyer-only MVP: block manage listings route
    {
      path: '/listings',
      name: 'ManageListingsPage',
      component: RedirectToLandingPage,
    },

    {
      path: '/traffic',
      name: 'TrafficDashboardPage',
      component: TrafficDashboardPage,
    },

    // Account settings
    {
      path: '/account',
      name: 'AccountSettingsPage',
      component: () => <NamedRedirect name="BuyerAccountPage" />,
    },
    {
      path: '/account/me',
      name: 'BuyerAccountPage',
      auth: true,
      authPage: 'LoginPage',
      component: BuyerAccountPage,
    },
    {
      path: '/account/preferences',
      name: 'BuyerPreferencesPage',
      auth: true,
      authPage: 'LoginPage',
      component: BuyerPreferencesPage,
    },
    {
      path: '/liked',
      name: 'LikedPage',
      auth: true,
      authPage: 'LoginPage',
      component: LikedPage,
    },
    {
      path: '/account/contact-details',
      name: 'ContactDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ContactDetailsPage,
      loadData: pageDataLoadingAPI.ContactDetailsPage?.loadData,
    },
    {
      path: '/account/change-password',
      name: 'PasswordChangePage',
      auth: true,
      authPage: 'LoginPage',
      component: PasswordChangePage,
    },

    // Buyer-only MVP: payouts are provider-only
    {
      path: '/account/payments',
      name: 'StripePayoutPage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="PaymentMethodsPage" />,
    },
    {
      path: '/account/payments/:returnURLType',
      name: 'StripePayoutOnboardingPage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="PaymentMethodsPage" />,
    },

    // Buyer payment methods
    {
      path: '/account/payment-methods',
      name: 'PaymentMethodsPage',
      auth: true,
      authPage: 'LoginPage',
      component: PaymentMethodsPage,
      loadData: pageDataLoadingAPI.PaymentMethodsPage?.loadData,
    },

    // Manage account
    {
      path: '/account/manage',
      name: 'ManageAccountPage',
      auth: true,
      authPage: 'LoginPage',
      component: ManageAccountPage,
    },

    // Legal
    {
      path: '/terms-of-service',
      name: 'TermsOfServicePage',
      component: TermsOfServicePage,
    },
    {
      path: '/privacy-policy',
      name: 'PrivacyPolicyPage',
      component: PrivacyPolicyPage,
    },

    // Styleguide
    {
      path: '/styleguide',
      name: 'Styleguide',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/g/:group',
      name: 'StyleguideGroup',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component',
      name: 'StyleguideComponent',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example',
      name: 'StyleguideComponentExample',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example/raw',
      name: 'StyleguideComponentExampleRaw',
      ...authForPrivateMarketplace,
      component: StyleguidePage,
      extraProps: { raw: true },
    },

    // Access / Not found
    {
      path: '/no-:missingAccessRight',
      name: 'NoAccessPage',
      component: NoAccessPage,
    },
    {
      path: '/notfound',
      name: 'NotFoundPage',
      component: props => <NotFoundPage {...props} />,
    },

    // Required endpoints
    {
      path: '/reset-password',
      name: 'PasswordResetPage',
      component: ResetPasswordPage,
    },
    {
      path: '/verify-email',
      name: 'EmailVerificationPage',
      auth: true,
      authPage: 'LoginPage',
      component: EmailVerificationPage,
      loadData: pageDataLoadingAPI.EmailVerificationPage?.loadData,
    },
    {
      path: '/preview',
      name: 'PreviewResolverPage',
      component: PreviewResolverPage,
    },
  ];
};

export default routeConfiguration;



